/**
 * MobileMenuDrawer — the small-screen counterpart of the SideNav bottom half.
 * The bottom tab bar only fits five destinations, so profile, the nav items it
 * cannot show, the More sections and Log Out live here.
 */
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import messageContext from '@wlb/shared/context/MessageContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { CONVERSATIONS_PAGE_SIZE } from '@wlb/shared/constants';
import { formatPagedUnreadBadgeCount } from '@wlb/shared/utils/unreadMessages';
import { useAuthGate } from '../feedback/AuthGate';
import { LogoutConfirmModal } from '../../features/auth/LogoutConfirmModal';
import { getProfileAvatarUrl, useCurrentProfile } from '../../features/profile/useCurrentProfile';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { Badge } from '../ui/Badge';
import { Avatar } from '../ui/Avatar';
import { CloseIcon, LogoutIcon, UserIcon } from '../ui/icons';
import { MORE_SECTIONS, MORE_SUBMENUS, NAV_ITEMS, type MoreItem } from './navigation';
import './layout.css';

/** Primary destinations the bottom tab bar has no room for. */
const MENU_ONLY_ITEMS: MoreItem[] = NAV_ITEMS.filter(item => item.hideOnMobileTabBar).map(
  ({ to, labelKey, icon, requiresAuth }) => ({ to, labelKey, icon, requiresAuth }),
);

interface MobileMenuDrawerProps {
  onClose: () => void;
}

const TITLE_ID = 'mobile-menu-title';

export function MobileMenuDrawer({ onClose }: MobileMenuDrawerProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { requireAuth } = useAuthGate();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const profile = useCurrentProfile();
  const avatarUrl = getProfileAvatarUrl(profile);
  const displayName = getUsernameDisplay(profile);
  // Messages is the one menu entry that carries unread state; read optionally
  // because the drawer also opens for anonymous visitors.
  const messages = useContext(messageContext);
  const unreadConversationsLabel = formatPagedUnreadBadgeCount(
    messages?.unreadConversationCount ?? 0,
    CONVERSATIONS_PAGE_SIZE,
  );
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  // Read through a ref so the key handler stays mounted across dialog toggles.
  const logoutConfirmRef = useRef(logoutConfirmOpen);

  closeRef.current = onClose;
  logoutConfirmRef.current = logoutConfirmOpen;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // The confirmation sits on top of the drawer and owns the keyboard while
      // it is open: Escape must cancel it, and Tab must stay in its buttons.
      if (logoutConfirmRef.current) return;
      if (event.key === 'Escape') {
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      // aria-modal only tells assistive tech the rest of the page is inert;
      // Tab still has to be kept inside the panel by hand.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = overflow;
    };
  }, []);

  const go = (to: string, requiresAuth: boolean): void => {
    onClose();
    if (requiresAuth && !isAuthenticated) {
      requireAuth(() => navigate(to));
      return;
    }
    navigate(to);
  };

  const renderItem = (item: MoreItem) => {
    const Icon = item.icon;
    const label = t(item.labelKey);
    const requiresAuth = item.requiresAuth !== false;
    const badge =
      item.to === '/messages' && unreadConversationsLabel ? (
        <Badge
          label={unreadConversationsLabel}
          description={t('sidebar.accessibility.unreadConversations', {
            amount: unreadConversationsLabel,
          })}
          className="mobile-menu__badge"
        />
      ) : null;

    if (requiresAuth && !isAuthenticated) {
      return (
        <li key={item.labelKey}>
          <button
            type="button"
            className="mobile-menu__link mobile-menu__link--locked"
            title={t('sidebar.accessibility.signInRequired', { label })}
            onClick={() => go(item.to, true)}
          >
            <Icon size={20} />
            <span>{label}</span>
          </button>
        </li>
      );
    }

    return (
      <li key={item.labelKey}>
        <NavLink
          to={item.to}
          onClick={onClose}
          className={({ isActive }) =>
            `mobile-menu__link${isActive ? ' mobile-menu__link--active' : ''}`
          }
        >
          <Icon size={20} />
          <span>{label}</span>
          {badge}
        </NavLink>
      </li>
    );
  };

  return (
    <div
      className="mobile-menu__backdrop"
      role="presentation"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <aside
        ref={panelRef}
        className="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
      >
        <header className="mobile-menu__header">
          <h2 id={TITLE_ID} className="mobile-menu__title">{t('home.rightBanner.more')}</h2>
          <button
            type="button"
            className="mobile-menu__close"
            onClick={onClose}
            aria-label={t('common.buttons.close')}
          >
            <CloseIcon size={20} />
          </button>
        </header>

        <div className="mobile-menu__body">
          {isAuthenticated ? (
            <button
              type="button"
              className="mobile-menu__profile"
              onClick={() => go('/profile', true)}
            >
              <Avatar
                src={avatarUrl}
                name={displayName}
                username={profile?.userName}
                seed={profile?.userGuid ?? profile?.userId}
                size="md"
                className="mobile-menu__avatar"
              />
              <span className="mobile-menu__profile-name">
                {displayName || t('home.rightBanner.myProfile')}
              </span>
            </button>
          ) : (
            <div className="mobile-menu__guest">
              <span className="mobile-menu__avatar" aria-hidden>
                <UserIcon size={20} />
              </span>
              <div className="mobile-menu__guest-info">
                <span className="mobile-menu__profile-name">{t('home.rightBanner.guestName')}</span>
                <button
                  type="button"
                  className="mobile-menu__guest-signup"
                  onClick={() => go('/signup', false)}
                >
                  {t('home.rightBanner.signUp')}
                </button>
              </div>
            </div>
          )}

          <ul className="mobile-menu__list">
            {MENU_ONLY_ITEMS.map(renderItem)}
            {MORE_SECTIONS.map(section => (
              <Fragment key={section[0].labelKey}>
                <li className="mobile-menu__divider" role="separator" />
                {section.map(renderItem)}
              </Fragment>
            ))}
          </ul>

          {MORE_SUBMENUS.map(menu => (
            <section key={menu.key} className="mobile-menu__section">
              <h3 className="mobile-menu__section-title">{t(menu.labelKey)}</h3>
              <ul className="mobile-menu__list">{menu.items.map(renderItem)}</ul>
            </section>
          ))}

          {isAuthenticated && (
            <button
              type="button"
              className="mobile-menu__link mobile-menu__link--logout"
              onClick={() => setLogoutConfirmOpen(true)}
            >
              <LogoutIcon size={20} />
              <span>{t('sidebar.logout')}</span>
            </button>
          )}
        </div>
      </aside>

      {logoutConfirmOpen && (
        <LogoutConfirmModal
          onClose={() => setLogoutConfirmOpen(false)}
          onConfirmed={onClose}
        />
      )}
    </div>
  );
}
